// Default seed data matching the user's spreadsheet and screenshots
import { calculateThreeTuesdays } from './TuesdayInstallmentModal';

// Robust local date parser that avoids UTC timezone offsets
export const parseDateParts = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }
  const parts = dateStr.split('-');
  if (parts.length >= 3) {
    return {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10),
      day: parseInt(parts[2], 10)
    };
  }
  const d = new Date(dateStr);
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
};

// Helper to get 'YYYY-MM' from date string
export const getYearMonth = (dateStr) => {
  const { year, month } = parseDateParts(dateStr);
  return `${year}-${String(month).padStart(2, '0')}`;
};

// Helper to format 'YYYY-MM' into human readable label like 'September 2026'
export const formatMonthYear = (yearMonthStr) => {
  if (!yearMonthStr) return '';
  const [y, m] = yearMonthStr.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

// Helper to format short month like 'Sep 2026' or 'Sep'
export const formatShortMonth = (yearMonthStr, includeYear = true) => {
  if (!yearMonthStr) return '';
  const [y, m] = yearMonthStr.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString('en-US', { month: 'short', ...(includeYear ? { year: 'numeric' } : {}) });
};

// Generates the Monday inflow breakdown for any specific month
export const generateMondaysForMonth = (yearMonthStr, expectedMonthly = 0, actualTotal = 0) => {
  const [y, m] = yearMonthStr.split('-').map(Number);
  const mondays = [];
  const daysInMonth = new Date(y, m, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(y, m - 1, day);
    if (d.getDay() === 1) { // 1 = Monday
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      mondays.push(dateStr);
    }
  }

  const count = mondays.length || 4;
  const weeklyExpected = Math.round((expectedMonthly / count) * 100) / 100;
  const weeklyActual = actualTotal > 0 ? Math.round((actualTotal / count) * 100) / 100 : 0;

  return mondays.map((dateStr, idx) => {
    const actual = idx === count - 1 && actualTotal > 0
      ? Math.round((actualTotal - (weeklyActual * (count - 1))) * 100) / 100
      : weeklyActual;
    return {
      week: idx + 1,
      date: dateStr,
      expected: weeklyExpected,
      actual: actual,
      status: actual >= weeklyExpected ? 'received' : (actual > 0 ? 'partial' : 'expected')
    };
  });
};

export const DEFAULT_INCOME_STREAMS = [
  {
    id: 'inc-1',
    name: 'Business Gross Revenue',
    description: 'Main SaaS subscriptions & merchant deposits',
    expectedAmount: 12500,
    actualReceived: 0, // Starts refreshed for the active month (Oct 2026)
    frequency: 'Monthly',
    mondays: generateMondaysForMonth('2026-10', 12500, 0),
    monthlyHistory: {
      '2026-07': {
        expectedAmount: 12500,
        actualReceived: 12100,
        mondays: generateMondaysForMonth('2026-07', 12500, 12100)
      },
      '2026-08': {
        expectedAmount: 12500,
        actualReceived: 13800,
        mondays: generateMondaysForMonth('2026-08', 12500, 13800)
      },
      '2026-09': {
        expectedAmount: 12500,
        actualReceived: 11200,
        mondays: [
          { week: 1, date: '2026-09-07', expected: 3125, actual: 3125, status: 'received' },
          { week: 2, date: '2026-09-14', expected: 3125, actual: 3000, status: 'received' },
          { week: 3, date: '2026-09-21', expected: 3125, actual: 2800, status: 'partial' },
          { week: 4, date: '2026-09-28', expected: 3125, actual: 2275, status: 'expected' }
        ]
      },
      '2026-10': {
        expectedAmount: 12500,
        actualReceived: 0,
        mondays: generateMondaysForMonth('2026-10', 12500, 0)
      }
    }
  },
  {
    id: 'inc-2',
    name: 'Secondary Revenue / Consulting',
    description: 'Advisory retainer & strategic consulting contracts',
    expectedAmount: 3400,
    actualReceived: 0,
    frequency: 'Monthly',
    mondays: generateMondaysForMonth('2026-10', 3400, 0),
    monthlyHistory: {
      '2026-07': {
        expectedAmount: 3400,
        actualReceived: 3200,
        mondays: generateMondaysForMonth('2026-07', 3400, 3200)
      },
      '2026-08': {
        expectedAmount: 3400,
        actualReceived: 3400,
        mondays: generateMondaysForMonth('2026-08', 3400, 3400)
      },
      '2026-09': {
        expectedAmount: 3400,
        actualReceived: 3400,
        mondays: [
          { week: 1, date: '2026-09-07', expected: 850, actual: 850, status: 'received' },
          { week: 2, date: '2026-09-14', expected: 850, actual: 850, status: 'received' },
          { week: 3, date: '2026-09-21', expected: 850, actual: 850, status: 'received' },
          { week: 4, date: '2026-09-28', expected: 850, actual: 850, status: 'received' }
        ]
      },
      '2026-10': {
        expectedAmount: 3400,
        actualReceived: 0,
        mondays: generateMondaysForMonth('2026-10', 3400, 0)
      }
    }
  },
  {
    id: 'inc-3',
    name: 'Investments / Other',
    description: 'Yield distributions & treasury bond dividends',
    expectedAmount: 950,
    actualReceived: 0,
    frequency: 'Monthly',
    mondays: generateMondaysForMonth('2026-10', 950, 0),
    monthlyHistory: {
      '2026-07': {
        expectedAmount: 950,
        actualReceived: 900,
        mondays: generateMondaysForMonth('2026-07', 950, 900)
      },
      '2026-08': {
        expectedAmount: 950,
        actualReceived: 950,
        mondays: generateMondaysForMonth('2026-08', 950, 950)
      },
      '2026-09': {
        expectedAmount: 950,
        actualReceived: 950,
        mondays: [
          { week: 1, date: '2026-09-07', expected: 237.5, actual: 237.5, status: 'received' },
          { week: 2, date: '2026-09-14', expected: 237.5, actual: 237.5, status: 'received' },
          { week: 3, date: '2026-09-21', expected: 237.5, actual: 237.5, status: 'received' },
          { week: 4, date: '2026-09-28', expected: 237.5, actual: 237.5, status: 'received' }
        ]
      },
      '2026-10': {
        expectedAmount: 950,
        actualReceived: 0,
        mondays: generateMondaysForMonth('2026-10', 950, 0)
      }
    }
  }
];

// Helper to get or initialize month-specific data for a stream
export const getStreamMonthData = (stream, yearMonthStr) => {
  if (!stream) return { expectedAmount: 0, actualReceived: 0, mondays: [] };
  const history = stream.monthlyHistory || {};
  if (history[yearMonthStr]) {
    return history[yearMonthStr];
  }
  // If no history exists for this month, default expected to base expectedAmount, actualReceived to 0 (refreshed for new month)
  const baseExpected = stream.expectedAmount || 0;
  return {
    expectedAmount: baseExpected,
    actualReceived: 0,
    mondays: generateMondaysForMonth(yearMonthStr, baseExpected, 0)
  };
};

// Synchronizes all income streams to reflect a specific viewing month
export const syncIncomeStreamsForMonth = (incomeStreams, yearMonthStr) => {
  if (!Array.isArray(incomeStreams)) return [];
  return incomeStreams.map(stream => {
    const history = { ...(stream.monthlyHistory || {}) };
    const monthData = history[yearMonthStr] || {
      expectedAmount: stream.expectedAmount || 0,
      actualReceived: 0,
      mondays: generateMondaysForMonth(yearMonthStr, stream.expectedAmount || 0, 0)
    };
    history[yearMonthStr] = monthData;

    return {
      ...stream,
      expectedAmount: monthData.expectedAmount,
      actualReceived: monthData.actualReceived,
      mondays: monthData.mondays,
      monthlyHistory: history
    };
  });
};

// Calculates comparison metrics for the last 3 consecutive months ending at refYearMonth
export const getLast3MonthsIncomeSummary = (incomeStreams, refYearMonth) => {
  if (!Array.isArray(incomeStreams) || incomeStreams.length === 0) {
    return { months: [], total3Months: 0, avgMonthlyActual: 0, momGrowth: 0, highestMonth: null };
  }

  const [y, m] = (refYearMonth || getYearMonth(getTodayDateStr())).split('-').map(Number);
  
  // Build the 3 months keys [Month -2, Month -1, Current Month]
  const monthsData = [];
  for (let offset = -2; offset <= 0; offset++) {
    const d = new Date(y, m - 1 + offset, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    const shortLabel = d.toLocaleString('en-US', { month: 'short' });
    const isCurrent = offset === 0;

    let totalExpected = 0;
    let totalActual = 0;
    const streamDetails = [];

    incomeStreams.forEach(stream => {
      const data = getStreamMonthData(stream, key);
      totalExpected += data.expectedAmount || 0;
      totalActual += data.actualReceived || 0;
      streamDetails.push({
        id: stream.id,
        name: stream.name,
        expected: data.expectedAmount || 0,
        actual: data.actualReceived || 0
      });
    });

    const realizationRate = totalExpected > 0 ? Math.round((totalActual / totalExpected) * 100) : 0;

    monthsData.push({
      key,
      label,
      shortLabel,
      isCurrent,
      totalExpected,
      totalActual,
      realizationRate,
      streams: streamDetails
    });
  }

  const total3Months = monthsData.reduce((sum, m) => sum + m.totalActual, 0);
  
  // Calculate average of prior completed months, plus current
  const completedMonths = monthsData.filter(m => !m.isCurrent);
  const avgCompleted = completedMonths.length > 0 
    ? Math.round(completedMonths.reduce((sum, m) => sum + m.totalActual, 0) / completedMonths.length)
    : Math.round(total3Months / 3);
  const avgMonthlyActual = Math.round(total3Months / 3);

  // Month-over-Month change between Month -2 and Month -1
  const m1 = monthsData[0].totalActual;
  const m2 = monthsData[1].totalActual;
  const priorMoM = m1 > 0 ? Math.round(((m2 - m1) / m1) * 1000) / 10 : 0;

  // Find month with highest earnings
  let highestMonth = monthsData[0];
  monthsData.forEach(m => {
    if (m.totalActual > (highestMonth?.totalActual || 0)) {
      highestMonth = m;
    }
  });

  return {
    months: monthsData,
    total3Months,
    avgMonthlyActual,
    avgCompleted,
    priorMoM,
    highestMonth
  };
};

// Helper to migrate and ensure user streams have monthlyHistory and are synced
export const migrateAndSyncIncomeStreams = (incomeStreams, refDateStr) => {
  if (!Array.isArray(incomeStreams) || incomeStreams.length === 0) return [];
  const currentYM = getYearMonth(refDateStr || getTodayDateStr());

  return incomeStreams.map(stream => {
    let history = stream.monthlyHistory ? { ...stream.monthlyHistory } : {};

    // If stream has no history at all, migrate its original values
    if (Object.keys(history).length === 0) {
      const origActual = typeof stream.actualReceived === 'number' ? stream.actualReceived : 0;
      const origExp = stream.expectedAmount || 0;
      
      // Default to Sept 2026 for the original actuals
      history['2026-07'] = {
        expectedAmount: origExp,
        actualReceived: Math.round(origExp * 0.95),
        mondays: generateMondaysForMonth('2026-07', origExp, Math.round(origExp * 0.95))
      };
      history['2026-08'] = {
        expectedAmount: origExp,
        actualReceived: Math.round(origExp * 1.05),
        mondays: generateMondaysForMonth('2026-08', origExp, Math.round(origExp * 1.05))
      };
      history['2026-09'] = {
        expectedAmount: origExp,
        actualReceived: origActual,
        mondays: stream.mondays || generateMondaysForMonth('2026-09', origExp, origActual)
      };
    }

    // Ensure the active month exists (refreshed to 0 for a brand new month like 2026-10)
    if (!history[currentYM]) {
      const baseExp = stream.expectedAmount || 0;
      history[currentYM] = {
        expectedAmount: baseExp,
        actualReceived: 0, // Fresh 0 at the start of a new month!
        mondays: generateMondaysForMonth(currentYM, baseExp, 0)
      };
    }

    const activeMonthData = history[currentYM];

    return {
      ...stream,
      expectedAmount: activeMonthData.expectedAmount,
      actualReceived: activeMonthData.actualReceived,
      mondays: activeMonthData.mondays,
      monthlyHistory: history
    };
  });
};

// Helper to sort bills strictly in order of due date (earliest to latest by default)
export const sortBillsByDueDate = (bills, order = 'asc') => {
  if (!Array.isArray(bills)) return [];
  return [...bills].sort((a, b) => {
    const mult = order === 'desc' ? -1 : 1;
    const dateA = a.dueDate || '';
    const dateB = b.dueDate || '';
    if (dateA !== dateB) return dateA.localeCompare(dateB) * mult;
    return (a.description || '').localeCompare(b.description || '');
  });
};

export const DEFAULT_EXPENSES = [
  {
    id: 'exp-6',
    description: 'Residential Mortgage & Property Tax Reserve',
    subDescription: 'Primary residential financing',
    category: 'Household',
    isInvoice: true,
    priority: 'Important (High Priority)',
    dueDate: '2026-09-05',
    amount: 2200.00,
    isPaid: true,
    hasThreeWeekPlan: false,
    repeatNextMonth: true,
    isExtended: false,
    extensionDate: '',
    extensionReason: ''
  },
  {
    id: 'exp-1',
    description: 'High-Priority Cloud & Server Infrastructure',
    subDescription: 'AWS & Kubernetes cluster monthly commitment',
    category: 'Business',
    isInvoice: true,
    priority: 'Important (High Priority)',
    dueDate: '2026-09-08',
    amount: 1450.00,
    isPaid: false,
    hasThreeWeekPlan: true,
    repeatNextMonth: true,
    isExtended: false,
    extensionDate: '',
    extensionReason: '',
    installments: [
      { number: 1, day: 'Tuesday 1', date: '2026-09-15', amount: 483.33, isPaid: false },
      { number: 2, day: 'Tuesday 2', date: '2026-09-22', amount: 483.33, isPaid: false },
      { number: 3, day: 'Tuesday 3', date: '2026-09-29', amount: 483.34, isPaid: false }
    ]
  },
  {
    id: 'exp-2',
    description: 'Commercial Office Lease & Workspace',
    subDescription: 'HQ tenancy agreement',
    category: 'Business',
    isInvoice: true,
    priority: 'Important (High Priority)',
    dueDate: '2026-09-15',
    amount: 3200.00,
    isPaid: false,
    hasThreeWeekPlan: true,
    repeatNextMonth: true,
    isExtended: false,
    extensionDate: '',
    extensionReason: '',
    installments: [
      { number: 1, day: 'Tuesday 1', date: '2026-09-15', amount: 1066.67, isPaid: false },
      { number: 2, day: 'Tuesday 2', date: '2026-09-22', amount: 1066.67, isPaid: false },
      { number: 3, day: 'Tuesday 3', date: '2026-09-29', amount: 1066.66, isPaid: false }
    ]
  },
  {
    id: 'exp-4',
    description: 'Team Payroll Software & Contractor Retainer',
    subDescription: 'Gusto payroll platform & UI contract fee',
    category: 'Business',
    isInvoice: true,
    priority: 'Average Important',
    dueDate: '2026-09-18',
    amount: 2100.00,
    isPaid: true,
    hasThreeWeekPlan: false,
    repeatNextMonth: true,
    isExtended: false,
    extensionDate: '',
    extensionReason: ''
  },
  {
    id: 'exp-7',
    description: 'Health & Comprehensive Liability Insurance',
    subDescription: 'Family medical & term life group coverage',
    category: 'Household',
    isInvoice: true,
    priority: 'Important (High Priority)',
    dueDate: '2026-09-19',
    amount: 880.00,
    isPaid: false,
    hasThreeWeekPlan: true,
    repeatNextMonth: true,
    isExtended: false,
    extensionDate: '',
    extensionReason: '',
    installments: [
      { number: 1, day: 'Tuesday 1', date: '2026-09-22', amount: 293.33, isPaid: false },
      { number: 2, day: 'Tuesday 2', date: '2026-09-29', amount: 293.33, isPaid: false },
      { number: 3, day: 'Tuesday 3', date: '2026-10-06', amount: 293.34, isPaid: false }
    ]
  },
  {
    id: 'exp-8',
    description: 'Household Utilities, Electric & Fiber Internet',
    subDescription: 'Municipal energy & gigabit connection',
    category: 'Household',
    isInvoice: true,
    priority: 'Average Important',
    dueDate: '2026-09-21',
    amount: 420.00,
    isPaid: true,
    hasThreeWeekPlan: false,
    repeatNextMonth: true,
    isExtended: false,
    extensionDate: '',
    extensionReason: ''
  },
  {
    id: 'exp-3',
    description: 'Key Inventory & Supplier Restock Batch',
    subDescription: 'Critical component manufacturing run',
    category: 'Business',
    isInvoice: true,
    priority: 'Important (High Priority)',
    dueDate: '2026-09-22',
    amount: 2850.00,
    isPaid: false,
    hasThreeWeekPlan: true,
    repeatNextMonth: false,
    isExtended: false,
    extensionDate: '',
    extensionReason: '',
    installments: [
      { number: 1, day: 'Tuesday 1', date: '2026-09-22', amount: 950.00, isPaid: false },
      { number: 2, day: 'Tuesday 2', date: '2026-09-29', amount: 950.00, isPaid: false },
      { number: 3, day: 'Tuesday 3', date: '2026-10-06', amount: 950.00, isPaid: false }
    ]
  },
  {
    id: 'exp-5',
    description: 'Digital Ad Campaigns & Paid Search Spend',
    subDescription: 'Google Ads & LinkedIn acquisition budget',
    category: 'Business',
    isInvoice: true,
    priority: 'Less Important',
    dueDate: '2026-09-25',
    amount: 950.00,
    isPaid: false,
    hasThreeWeekPlan: false,
    repeatNextMonth: false,
    isExtended: false,
    extensionDate: '',
    extensionReason: ''
  },
  {
    id: 'exp-9',
    description: 'Grocery & Family Provisions Monthly Pool',
    subDescription: 'Household food & weekly staples allowance',
    category: 'Household',
    isInvoice: false,
    priority: 'Average Important',
    dueDate: '2026-09-26',
    amount: 950.00,
    isPaid: false,
    hasThreeWeekPlan: false,
    repeatNextMonth: true,
    isExtended: false,
    extensionDate: '',
    extensionReason: ''
  },
  {
    id: 'exp-10',
    description: 'Vehicle Maintenance & Fleet Fuel Card',
    subDescription: 'Scheduled auto service & transport expenses',
    category: 'Others',
    isInvoice: true,
    priority: 'Average Important',
    dueDate: '2026-09-28',
    amount: 630.00,
    isPaid: false,
    hasThreeWeekPlan: false,
    repeatNextMonth: true,
    isExtended: true,
    extensionDate: '2026-10-05',
    extensionReason: 'Requested 1-week extension with garage billing dept'
  },
  {
    id: 'exp-11',
    description: 'Annual Software Licensing & Security Audit',
    subDescription: 'Penetration testing & compliance review fee',
    category: 'Business',
    isInvoice: true,
    priority: 'Less Important',
    dueDate: '2026-10-15',
    amount: 1800.00,
    isPaid: false,
    hasThreeWeekPlan: false,
    repeatNextMonth: false,
    isExtended: false,
    extensionDate: '',
    extensionReason: ''
  },
  {
    id: 'exp-12',
    description: 'Charitable Giving & Community Development',
    subDescription: 'Quarterly community STEM sponsorship',
    category: 'Others',
    isInvoice: false,
    priority: 'Less Important',
    dueDate: '2026-11-01',
    amount: 500.00,
    isPaid: false,
    hasThreeWeekPlan: false,
    repeatNextMonth: false,
    isExtended: false,
    extensionDate: '',
    extensionReason: ''
  }
];

export const DEFAULT_CREDIT_CARD = {
  balance: 4878.09,
  lastInterest: 46.63,
  lastInterestDate: '2026-09-05',
  gracePeriodDays: 30,
  plannedRepayment: 1000.00,
  transactions: [
    {
      id: 'tx-1',
      date: '2026-09-05',
      type: 'Opening Balance',
      amount: 4878.09,
      gracePeriodDays: 0,
      dueDate: '2026-09-05',
      repaymentApplied: 0,
      interestBearing: 4878.09,
      notes: 'Existing interest-bearing balance'
    },
    {
      id: 'tx-2',
      date: '2026-09-15',
      type: 'New Loan / Spend',
      amount: 500.00,
      gracePeriodDays: 30,
      dueDate: '2026-10-15',
      repaymentApplied: 0,
      interestBearing: 0,
      notes: 'Grace period active until due date; if unpaid by deadline, adds to interest balance'
    },
    {
      id: 'tx-3',
      date: '2026-09-20',
      type: 'Repayment',
      amount: -1000.00,
      gracePeriodDays: 0,
      dueDate: '-',
      repaymentApplied: -1000.00,
      interestBearing: -1000.00,
      notes: 'Applied directly against oldest balance'
    }
  ]
};

// Helper to get today's local date string formatted as YYYY-MM-DD
export const getTodayDateStr = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const DEFAULT_CONFIG = {
  referenceDate: getTodayDateStr(),
  autoSyncRealDate: true,
  currencySymbol: '$',
  safeDebtRatio: 0.30, // 30% safe debt capacity cap
  annualGrowthRate: 0.05,
  categoryBudgets: {
    Business: 12000,
    Household: 5000,
    Others: 2000
  }
};

export const AUTH_STORAGE_KEY = 'bill_tracker_auth_creds_v2';
export const USERS_STORAGE_KEY = 'bill_tracker_all_users_v2';
export const CURRENT_USER_KEY = 'bill_tracker_current_user_v2';
export const SESSION_KEY = 'bill_tracker_is_logged_in_v2';
export const DATA_STORAGE_KEY = 'bill_tracker_financial_ledger_v2';

// Helper to get user-specific storage key
export const getUserLedgerKey = (username) => {
  const safeName = (username || 'default').trim().toLowerCase();
  return `bill_tracker_ledger_${safeName}`;
};

// Helper to load ledger isolated per user
export const loadUserLedger = (username) => {
  const safeName = (username || 'default').trim().toLowerCase();
  const key = getUserLedgerKey(safeName);
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.expenses && Array.isArray(parsed.expenses)) {
        parsed.expenses = sortBillsByDueDate(parsed.expenses);
      }
      // Migrate and sync income streams to ensure active month is fresh and 3-month history is available
      if (parsed.incomeStreams && Array.isArray(parsed.incomeStreams)) {
        parsed.incomeStreams = migrateAndSyncIncomeStreams(parsed.incomeStreams, parsed.config?.referenceDate || getTodayDateStr());
      }
      // Ensure creditCard exists even if upgrading from older saved ledger
      if (!parsed.creditCard) {
        parsed.creditCard = (safeName === 'adedeji' || safeName === 'admin' || safeName === 'default')
          ? DEFAULT_CREDIT_CARD
          : { balance: 0, lastInterest: 0, lastInterestDate: '', gracePeriodDays: 30, plannedRepayment: 0, transactions: [] };
      }
      // Ensure categoryBudgets and auto-sync date exist on config
      if (!parsed.config) {
        parsed.config = { ...DEFAULT_CONFIG, referenceDate: getTodayDateStr(), autoSyncRealDate: true };
      } else {
        if (!parsed.config.categoryBudgets) {
          parsed.config.categoryBudgets = { Business: 12000, Household: 5000, Others: 2000 };
        }
        // Auto-update to real today date if autoSyncRealDate is enabled (or undefined) OR if still holding old demo date '2026-09-16'
        if (parsed.config.autoSyncRealDate !== false || parsed.config.referenceDate === '2026-09-16') {
          parsed.config.referenceDate = getTodayDateStr();
          parsed.config.autoSyncRealDate = true;
        }
      }
      return parsed;
    }
  } catch (e) {
    console.error('Error loading ledger for user:', username, e);
  }

  // If this is the primary owner account 'adedeji' or 'admin', initialize with the rich demo ledger
  if (safeName === 'adedeji' || safeName === 'admin' || safeName === 'default') {
    const today = getTodayDateStr();
    const initialData = {
      expenses: DEFAULT_EXPENSES,
      incomeStreams: syncIncomeStreamsForMonth(DEFAULT_INCOME_STREAMS, getYearMonth(today)),
      creditCard: DEFAULT_CREDIT_CARD,
      config: { ...DEFAULT_CONFIG, referenceDate: today, autoSyncRealDate: true }
    };
    try {
      localStorage.setItem(key, JSON.stringify(initialData));
    } catch (e) {}
    return initialData;
  }

  // For any other new user, provide a clean, empty ledger for their own personal bills!
  const emptyUserData = {
    expenses: [],
    incomeStreams: [],
    creditCard: {
      balance: 0,
      lastInterest: 0,
      lastInterestDate: '',
      gracePeriodDays: 30,
      plannedRepayment: 0,
      transactions: []
    },
    config: { ...DEFAULT_CONFIG, referenceDate: getTodayDateStr(), autoSyncRealDate: true }
  };
  try {
    localStorage.setItem(key, JSON.stringify(emptyUserData));
  } catch (e) {}
  return emptyUserData;
};

// Helper to save user-specific ledger
export const saveUserLedger = (username, data) => {
  const key = getUserLedgerKey(username);
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error('Error saving ledger for user:', username, e);
  }
};

/**
 * Evaluates recurring monthly expenses.
 * Rule: If repeatNextMonth is true, starting from the first day of the following month,
 * it reflects again. It will NEVER appear before the new month starts.
 */
export const syncRecurringExpenses = (expenses, referenceDate) => {
  if (!Array.isArray(expenses) || !referenceDate) return expenses;

  const { year: refYear, month: refMonth } = parseDateParts(referenceDate);

  let updatedList = [...expenses];
  let hasChanges = false;

  expenses.forEach(bill => {
    if (!bill.repeatNextMonth) return;

    const { year: bYear, month: bMonth, day: originalDay } = parseDateParts(bill.dueDate);

    // Months difference between current reference date and bill due date
    const monthDiff = (refYear - bYear) * 12 + (refMonth - bMonth);

    // Only reflect starting from the first day of the following month (monthDiff >= 1)
    if (monthDiff >= 1) {
      const daysInRefMonth = new Date(refYear, refMonth, 0).getDate();
      const targetDay = Math.min(originalDay, daysInRefMonth);
      const targetDateStr = `${refYear}-${String(refMonth).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`;

      // Stable recurring identifier for this month instance
      const parentId = bill.parentRecurringId || bill.id;
      const recurringInstanceId = `${parentId}-recur-${refYear}-${refMonth}`;

      // Check if an instance already exists for this month
      const alreadyExists = updatedList.some(b => 
        b.id === recurringInstanceId ||
        ((b.parentRecurringId === parentId || b.id === parentId) && b.dueDate.startsWith(`${refYear}-${String(refMonth).padStart(2, '0')}`))
      );

      if (!alreadyExists) {
        const amountNum = parseFloat(bill.amount) || 0;
        const hasPlan = bill.priority?.startsWith('Important') ? true : bill.hasThreeWeekPlan;
        const installments = hasPlan
          ? calculateThreeTuesdays(targetDateStr, amountNum)
          : [];

        const newRecurringBill = {
          ...bill,
          id: recurringInstanceId,
          parentRecurringId: parentId,
          dueDate: targetDateStr,
          amount: amountNum,
          isPaid: false,
          isExtended: false,
          extensionDate: '',
          extensionReason: '',
          hasThreeWeekPlan: hasPlan,
          installments,
          repeatNextMonth: true,
          isRecurringInstance: true
        };

        updatedList.push(newRecurringBill);
        hasChanges = true;
      }
    }
  });

  return hasChanges ? sortBillsByDueDate(updatedList) : sortBillsByDueDate(expenses);
};


